/**
 * cconfigService — singleton for safe cconfig reads and writes.
 *
 * Problem it solves: multiple pages (Reports, POS Mapping, etc.) each do a
 * full GET → modify their keys → POST the entire cconfig object back.
 * If two pages save concurrently, whichever posts last silently wipes the
 * other page's keys.
 *
 * This service owns the cconfig state. All writes go through `setKeys()`,
 * which fetches the latest cconfig first, merges the new keys in, then posts
 * the merged result — so no page's data is ever overwritten by another.
 */

import axios from 'axios';
import Configuration from '../constant/configuration';
import { getToken } from './tokenStorage';

// ── Constants ─────────────────────────────────────────────────────────────────

/**
 * The mDash backend enforces a hard 32 KB limit on the full cconfig object.
 * We reject writes at 28 KB to leave headroom for concurrent updates and to
 * surface the problem early with a clear error rather than a cryptic 4xx.
 */
const CCONFIG_SOFT_LIMIT_BYTES = 28 * 1024; // 28 KB safety threshold (backend hard limit: 32 KB)

/** Error message thrown when a write would exceed the soft size limit. */
export const CCONFIG_SIZE_LIMIT_ERROR = 'CCONFIG_SIZE_LIMIT';

/**
 * True when `err` is the size-limit rejection. Callers use this to tell the
 * user storage is full instead of inviting a retry that can never succeed.
 */
export const isCconfigSizeLimitError = (err) =>
  !!err && err.message === CCONFIG_SIZE_LIMIT_ERROR;

// ── Internal state ────────────────────────────────────────────────────────────

let _cache = null;           // last-known full cconfig object
let _writeQueue = null;      // serialise concurrent writes via promise chain
let _generation = 0;         // bumped by invalidateCache(); see fetchCconfig

// ── Helpers ───────────────────────────────────────────────────────────────────

function mdashUrl() {
  const token = getToken();
  return `${Configuration.mdashURL}/customer?access_token=${encodeURIComponent(token)}`;
}

async function fetchCconfig() {
  const generation = _generation;
  const res = await axios.get(mdashUrl());
  const cconfig = (res.data && res.data.cconfig) || {};
  // A read that was in flight across invalidateCache() (sign-out, a backend
  // token write) answers its caller but must not re-seed the cache — after a
  // sign-out it would hand the previous account's cconfig to the next one.
  if (generation === _generation) _cache = cconfig;
  return cconfig;
}

/**
 * Measure the serialised byte size of a cconfig object and reject if it
 * exceeds the soft limit. TextEncoder gives accurate UTF-8 byte counts.
 *
 * @param {object} cconfig  The fully-merged cconfig object about to be POSTed.
 * @returns {void}  Throws if the size exceeds CCONFIG_SOFT_LIMIT_BYTES.
 */
function assertCconfigSize(cconfig) {
  const json = JSON.stringify(cconfig);
  const byteSize = new TextEncoder().encode(json).length;
  if (byteSize > CCONFIG_SOFT_LIMIT_BYTES) {
    throw new Error(CCONFIG_SIZE_LIMIT_ERROR);
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Read the full cconfig object.
 * Uses the in-memory cache when available; otherwise fetches from mDash.
 *
 * @returns {Promise<object>}
 */
export async function getCconfig() {
  if (_cache !== null) return { ..._cache };
  return fetchCconfig();
}

/**
 * Merge `updates` into the existing cconfig and save.
 * Performs a fresh GET first so we never overwrite keys set by other pages.
 * Concurrent calls are serialised — each waits for the previous to finish.
 *
 * Rejects with Error('CCONFIG_SIZE_LIMIT') if the merged object exceeds
 * CCONFIG_SOFT_LIMIT_BYTES (28 KB) before sending, so callers' .catch paths
 * fire cleanly without a network round-trip. The backend hard limit is 32 KB.
 *
 * @param {object} updates  Plain object of top-level cconfig keys to set.
 * @returns {Promise<void>}
 */
export function setKeys(updates) {
  const doWrite = async () => {
    // Always re-fetch before writing to get the freshest state
    const current = await fetchCconfig();
    const merged = { ...current, ...updates };

    // Guard: reject before sending if merged object exceeds the soft size limit
    assertCconfigSize(merged);

    await axios.post(
      mdashUrl(),
      { cconfig: merged },
      { headers: { 'Content-Type': 'application/json' } }
    );

    _cache = merged;
  };

  // Chain onto the existing queue so concurrent callers don't race
  _writeQueue = (_writeQueue || Promise.resolve())
    .then(doWrite, doWrite);   // recover from previous failure so queue never stalls

  return _writeQueue;
}

/**
 * Delete one or more top-level cconfig keys and save.
 *
 * Rejects with Error('CCONFIG_SIZE_LIMIT') if the remaining object exceeds
 * CCONFIG_SOFT_LIMIT_BYTES (28 KB) — in practice this should never fire on
 * a delete path, but the guard is applied consistently on every write.
 *
 * @param {...string} keys  Key names to remove.
 * @returns {Promise<void>}
 */
export function deleteKeys(...keys) {
  const doDelete = async () => {
    const current = await fetchCconfig();
    const merged = { ...current };
    keys.forEach(k => delete merged[k]);

    // Guard: reject before sending if object exceeds the soft size limit
    assertCconfigSize(merged);

    await axios.post(
      mdashUrl(),
      { cconfig: merged },
      { headers: { 'Content-Type': 'application/json' } }
    );

    _cache = merged;
  };

  _writeQueue = (_writeQueue || Promise.resolve())
    .then(doDelete, doDelete);

  return _writeQueue;
}

/**
 * Invalidate the in-memory cache.
 * Call this after external code writes to cconfig directly (e.g. globalAccess).
 */
export function invalidateCache() {
  _generation++;
  _cache = null;
}
